import { getScrollDirection } from "@/utils/scrollDirection";
import { Cross, Pill, PillSelector } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import { useAppliedPageData } from "..";
import { useDashboardContext } from "../..";
import FilterLoader from "../../filterLoader/filterLoader";
import { convertCountInThousands } from "../../utils/convertCountInThousands";
import {
  AppliedPageContextProps,
  AppliedPageDataType,
  FilterStatusType,
  SelectedFilter,
} from "../types";
import "./index.css";

interface DashboardPropsType {
  isMobile: boolean;
}

const Filters = () => {
  const {
    appliedJobData,
    setAppliedJobData,
    handleEventTracking,
    cleverTapPropertyValues,
  }: AppliedPageContextProps = useAppliedPageData();

  const filters =
    appliedJobData?.filterStatus
      ?.filter((data: FilterStatusType, index: number) => {
        return data.count > 0;
      })
      .map((data: FilterStatusType, index: number) => {
        return {
          id: data.uuid,
          selected: data.selected,
          text: data.text,
          count: data.count,
        };
      }) || [];

  const filterName = ["Jobs by"];

  const [showFilter, setShowFilter] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      const direction = getScrollDirection();
      if (direction === "down") {
        setShowFilter(false);
      } else if (direction === "up") {
        setShowFilter(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleFilter = (
    selectedOption: SelectedFilter[] | undefined,
    type: string,
  ) => {
    setAppliedJobData((prev: AppliedPageDataType) => {
      return {
        ...prev,
        selectedFilter:
          selectedOption && type == "select"
            ? [...selectedOption]
            : type !== "remove"
              ? [...(prev?.selectedFilter || [])]
              : [],
        filterDisable: true,
        isLoadingJobDesc: true,
      };
    });
    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name: `${cleverTapPropertyValues[appliedJobData.activeTab as keyof typeof cleverTapPropertyValues]}${type == "remove" ? "" : selectedOption?.[0]?.text ? ` ${selectedOption[0].text}` : ""}`,
      },
    });
  };

  const fetchFilterOptions = (filterType: string) => {
    if (filterType === null || filterType === undefined || filterType === "")
      return;

    switch (filterType) {
      case "Jobs by":
        return (
          <div className="flex flex-col gap-3 text-sm">
            <div
              className={`flex w-full justify-between ${isMobile && "border-b-2 p-3"}`}
            >
              <div className="">
                <span
                  className={`font-sans text-sm font-normal not-italic leading-5 !text-[#1D1934]`}
                >
                  {filterType}
                </span>
                {appliedJobData.selectedFilter?.length > 0 && (
                  <span className="text-content-placeholder ml-1 text-sm">
                    ({appliedJobData.selectedFilter?.length || 0})
                  </span>
                )}
              </div>

              {isMobile ? (
                <Cross
                  width="16"
                  onClick={() => {
                    handleFilter(undefined, "remove");
                  }}
                />
              ) : (
                appliedJobData.selectedFilter?.length > 0 && (
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      handleFilter(undefined, "remove");
                    }}
                  >
                    <span className="text-xs font-normal not-italic leading-4 text-[#6E00BE]">
                      Clear
                    </span>
                  </div>
                )
              )}
            </div>
            <div
              className={`relative flex flex-col ${isMobile ? "" : "border-bt-2"}`}
            >
              <div
                className={`relative w-full ${isMobile ? "h-[550px] p-3" : ""}`}
              >
                <div>
                  {filters && filters?.length > 0
                    ? filters
                        ?.slice(0, isMobile ? filters?.length : 5)
                        ?.map((data: SelectedFilter) => (
                          <li
                            key={`${data?.count} ${data?.text}`}
                            className="mb-3 flex items-start gap-2 text-xs font-normal not-italic leading-4 text-[#17142A]"
                          >
                            <input
                              id={data.id}
                              type="radio"
                              checked={data.selected}
                              onChange={(e) => {
                                handleFilter(
                                  [{ ...data, selected: e.target.checked }],
                                  "select",
                                );
                              }}
                              disabled={appliedJobData.filterDisable}
                              className="accent-surface-contrast"
                              name="jobBasedOn"
                            />
                            {data.count ? (
                              <div>
                                {data.text}
                                <span className="text-content-placeholder ml-1">
                                  {`(${convertCountInThousands(data.count)})`}
                                </span>
                              </div>
                            ) : (
                              <span className="">{data.name}</span>
                            )}
                          </li>
                        ))
                    : null}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const { isMobile }: DashboardPropsType = useDashboardContext();

  return (
    <>
      {appliedJobData.isLoadingJobDesc ? (
        <FilterLoader isMobile={isMobile} />
      ) : (
        <div>
          {isMobile && appliedJobData.filterStatusPresent && (
            <div
              className={`dashboard-filter-container ${showFilter ? "" : "hidden"} bg-surface-primary-normal fixed bottom-0 z-[999999] flex w-full items-center gap-4 overflow-x-scroll border bg-white px-3 py-4 shadow-[0px_-4px_8px_-2px_rgba(0,0,0,0.03),0px_-2px_2px_-2px_rgba(0,0,0,0.05)]`}
            >
              <div className="relative flex w-max items-center gap-4 md:w-auto">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-[4px]">
                    <p className="text-[10px] font-normal leading-[14px] tracking-[0.4px] text-[#17142A]">
                      Filter {filterName[0]}
                    </p>
                  </div>
                  <div className="pill-container">
                    <PillSelector
                      disabled={appliedJobData.filterDisable}
                      getValue={(selectedOption: SelectedFilter[]) => {
                        handleFilter(selectedOption, "select");
                      }}
                      options={filters}
                      rightIcon={
                        <Cross
                          className="cursor-pointer"
                          onClick={() => {
                            handleFilter(undefined, "remove");
                          }}
                        />
                      }
                      rightIconDivider={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {!isMobile && appliedJobData.filterStatusPresent && (
            <div
              className={`border-1 relative flex w-full flex-col ${isMobile ? "gap-2" : "gap-4"} border-jobCardBorder rounded-2xl border pb-4 pl-3 pr-3 pt-6`}
            >
              <div className="flex flex-col px-3">
                <span className="text-base font-bold not-italic leading-6 text-[#1D1934]">
                  All Filters
                  <hr className="text-surface-primary-normal mt-3 text-xs" />
                </span>
              </div>
              {appliedJobData.selectedFilter?.length > 0 ? (
                <div className="flex flex-col gap-2 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm">
                      {appliedJobData.selectedFilter?.length} filters applied
                    </span>
                    <span
                      className="cursor-pointer text-xs font-normal not-italic leading-4 text-[#6E00BE]"
                      onClick={() => {
                        handleFilter(undefined, "remove");
                      }}
                    >
                      Clear All
                    </span>
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-2 border-b py-2">
                    {appliedJobData?.selectedFilter?.length > 0 &&
                      appliedJobData.selectedFilter.map(
                        (filterType: SelectedFilter) => {
                          return (
                            <Pill
                              key={filterType}
                              id={filterType}
                              displayName={
                                <span className="text-content-secondary line-clamp-1 flex max-w-12">
                                  <span className="truncate">
                                    {filterType?.text || ""}
                                  </span>
                                </span>
                              }
                              size="small"
                              selected={filterType.selected || false}
                              rightIcon={
                                <Cross
                                  width={16}
                                  height={16}
                                  onClick={() => {
                                    handleFilter(undefined, "remove");
                                  }}
                                />
                              }
                              customClass="bg-surface-secondary-normal border-brand-secondary text-content-secondary"
                            />
                          );
                        },
                      )}
                  </div>
                </div>
              ) : null}
              <div className="no-scrollbar relative flex flex-col overflow-auto pb-2 pl-3 pr-3 pt-2">
                {filterName.map((filterType) => {
                  return fetchFilterOptions(filterType);
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default React.memo(Filters);
